// ✅ src/pages/Asset/ModelMaster.jsx
// FULL FILE - API INTEGRATED: CODE + GETBYID + TRANSEXIST + SAVE/UPDATE + LOCALSTORAGE HEADERS

import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPlus, FaSave } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/modelMaster";

const ModelMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ read ?id= for edit mode
  const query = new URLSearchParams(location.search);
  const modelId = Number(query.get("id") || 0);
  const isEdit = modelId > 0;

  const [loading, setLoading] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [formData, setFormData] = useState({
    id: 0,
    modelName: "",
    modelCode: "",
    remarks: "",
    active: true,
  });

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const requiredClass = "text-red-500";

  // ---------------------- API calls ----------------------

  const loadNewCode = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/code`, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to generate code");
        return;
      }

      setFormData((p) => ({
        ...p,
        id: 0,
        modelCode: data.modelCode || "",
      }));
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Code API error");
    }
  };

  const loadById = async (id) => {
    try {
      setLoading(true);

      const { data } = await axios.get(`${API_BASE}/getById/${id}`, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load model");
        return;
      }

      const d = data.data || {};
      setFormData({
        id: d.id || id,
        modelName: d.modelName || "",
        modelCode: d.modelCode || "",
        remarks: d.remarks || "",
        active: d.active === true,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "GetById API error");
    } finally {
      setLoading(false);
    }
  };

  const checkTransExist = async (id) => {
    try {
      const { data } = await axios.get(`${API_BASE}/transExist/${id}`, {
        headers: commonHeaders,
      });

      if (!data?.success) return;

      const exists = data.exists === true;
      setSaveDisabled(exists);

      if (exists) {
        toast.info("Transactions exist. Save is disabled for this record.");
      }
    } catch {
      // ignore
    }
  };

  // ---------------------- initial load ----------------------

  useEffect(() => {
    if (isEdit) {
      loadById(modelId);
      checkTransExist(modelId);
    } else {
      setSaveDisabled(false);
      setFormData((p) => ({
        ...p,
        id: 0,
        modelName: "",
        remarks: "",
        active: true,
      }));
      loadNewCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, finYearId, finYear, branchId, branchType, userId]);

  // ---------------------- handlers ----------------------

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    if (!formData.modelName.trim()) {
      toast.error("Model Name is required!");
      return false;
    }
    if (!formData.modelCode.trim()) {
      toast.error("Model Code is required!");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (saveDisabled) {
      toast.error("Save disabled (transactions exist).");
      return;
    }

    if (!validate()) return;

    try {
      setLoading(true);

      const body = {
        id: formData.id || 0,
        modelCode: formData.modelCode,
        modelName: formData.modelName,
        remarks: formData.remarks,
        active: formData.active,
      };

      const { data } = await axios.post(`${API_BASE}/save`, body, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Save failed");
        return;
      }

      toast.success(data?.message || "Saved Successfully");
      navigate("/Asset/ModelMasterListing");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Save API error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = async () => {
    setFormData({
      id: 0,
      modelName: "",
      modelCode: "",
      remarks: "",
      active: true,
    });
    setSaveDisabled(false);
    await loadNewCode();
    toast.info("Ready for new entry");
  };

  const handleBack = () => {
    navigate(-1);
  };

  // ---------------------- UI ----------------------

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Model Master</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {isEdit ? "Edit Model" : "Add Model"}
              </h1>
            </div>

            <div className="flex items-end gap-3">
              <button
                onClick={handleBack}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                }`}
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl border p-6 ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Model Name <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="modelName"
                  value={formData.modelName}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter model name"
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Model Code
                </label>
                <input
                  type="text"
                  name="modelCode"
                  value={formData.modelCode}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter model code"
                  disabled
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <label className={`${labelClass} text-right md:text-right pt-2`}>
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows={4}
                  className={inputClass}
                  placeholder="Enter remarks"
                  disabled={loading}
                />
              </div>

              <div className="border-t pt-6 mt-6 border-gray-200 dark:border-gray-700">
                <div className="flex justify-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      disabled={loading}
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Active
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-center gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSave}
                  disabled={loading || saveDisabled}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg transition-all duration-200 ${
                    loading || saveDisabled
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : isDark
                      ? "bg-blue-700 hover:bg-blue-600 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                  title={saveDisabled ? "Transactions exist. Save disabled." : ""}
                >
                  <FaSave />
                  {loading ? "Saving..." : "Save"}
                </button>

                <button
                  onClick={handleAddNew}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaPlus />
                  Add New
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default ModelMaster;